import { ContextService } from "../sdServices/context/context-service";

export const recoveryBroadcastEvent = "SdRecoveryBroadcastEvent";

interface ScopeRecoveryData {
  relativeId: string;
  fullId?: string;
  proceedWithRecovery: boolean;
}
export interface RecoveryInstanceOptions {
  /**
   * Recovery calls from parents will not cascade to children. Instead you will have to explicitly
   * call recoverChildren to recover them.
   */
  waitForExplicitChildRecover?: boolean;
}

const fullRecoveryIdSeparator = "|";
/** A Utillity class that assists with the recovery of a component. */
export class RecoveryManager {
  private fullRecoveryId: string;
  // @ngInject
  constructor(private contextService: ContextService, private $scope: ng.IScope, private relativeRecoveryId: string) {
    this.$scope.$sdrecovery = this.$scope.$sdrecovery || { relativeId: relativeRecoveryId };
  }

  /**
   * Register a callback for recovery.
   * @param callback The callback for whenRecover is called whenever the recovery process has been triggered. It will be called with a data object, which is whatever data that was stored .
   * @param recoveryOptions Any options to take into account during recovery.
   */
  public whenRecover(callback: (data: any) => void, recoveryOptions?: RecoveryInstanceOptions): void {
    recoveryOptions = recoveryOptions || {};
    let parent = this.$scope.$parent;
    let shouldRecover = this.contextService.current.wasRecovered;
    while (shouldRecover && parent) {
      let parentRecoveryData = this.getScopeRecoveryData(parent);
      if (parentRecoveryData) {
        shouldRecover = parentRecoveryData.proceedWithRecovery;
        break;
      }
      parent = parent.$parent;
    }
    if (shouldRecover) {
      this.recoverInternal(callback, recoveryOptions);
    } else {
      let stopListening = this.$scope.$on(recoveryBroadcastEvent, (event, recoverId: string) => {
        if (this.getImmediateParentRecoveryId() == recoverId) {
          this.recoverInternal(callback, recoveryOptions);
          stopListening();
        }
      });
    }
  }

  private getImmediateParentRecoveryId() {
    let recoveryIdComponents = this.getRecoveryId().split(fullRecoveryIdSeparator);
    if (recoveryIdComponents.length > 1) {
      return recoveryIdComponents.slice(0, recoveryIdComponents.length - 1).join(fullRecoveryIdSeparator);
    }
    return undefined;
  }

  /**
   * Recover children of this component. Only really useful if the option 'waitForExplicitChildRecover' was specified.
   */
  public recoverChildren(): void {
    this.getScopeRecoveryData(this.$scope).proceedWithRecovery = true;
    this.$scope.$broadcast(recoveryBroadcastEvent, this.getRecoveryId());
  }

  /**
   * Save some datat that you'd like to restore at a later point.
   * @param data the data to save.
   */
  public saveState(data: any): void {
    let recoveryId = this.getRecoveryId();
    this.contextService.setExtendedContextData(recoveryId, JSON.stringify(data));
  }

  private recoverInternal(callback: (data: any) => void, recoveryOptions: RecoveryInstanceOptions) {
    if (!recoveryOptions.waitForExplicitChildRecover) {
      this.recoverChildren();
    }
    let unparsedData = this.contextService.getExtendedContextData(this.getRecoveryId());
    if (unparsedData) {
      let data = JSON.parse(unparsedData);
      callback(data);
    }
  }

  private getScopeRecoveryData(scope: ng.IScope): ScopeRecoveryData {
    return scope.$sdrecovery;
  }

  private getRecoveryId() {
    if (this.fullRecoveryId) {
      return this.fullRecoveryId;
    }
    let recoveryIds: string[] = [];
    let parent = this.$scope.$parent;
    while (parent) {
      let parentRecoveryData = this.getScopeRecoveryData(parent);
      if (parentRecoveryData) {
        if (parentRecoveryData.fullId) {
          recoveryIds.push(parentRecoveryData.fullId);
          break;
        }
        recoveryIds.push(parentRecoveryData.relativeId);
      }
      parent = parent.$parent;
    }
    let recoveryId = "";
    while (recoveryIds.length > 0) {
      recoveryId += recoveryIds.pop() + fullRecoveryIdSeparator;
    }
    recoveryId += this.relativeRecoveryId;
    this.getScopeRecoveryData(this.$scope).fullId = recoveryId;
    this.fullRecoveryId = recoveryId;
    return recoveryId;
  }
}
